/**
 * Mug painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_paint_crafts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const mugPaintingIdeasPack: ToilePack = {
    slug: 'mug-painting-ideas',
    category: 'craft',
    name: 'Mug painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'face', label: 'Face', subject: 'a mug painted with a cute face'},
        {key: 'floral', label: 'Floral', subject: 'a mug painted with daisies'},
        {key: 'name', label: 'Name', subject: 'a mug painted with a name reading "Mom"'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel', style: 'hand-painted with acrylic enamel in smooth opaque strokes', palette: 'soft pastel pink, mint and butter yellow on a white glazed mug', framing: 'standing on a light oak table beside a small vase, a three-quarter view with the handle to the right'},
        {key: 'bold', label: 'Bold pop', style: 'painted in thick flat porcelain paint with clean black outlines', palette: 'bold primary red, blue and yellow on white', framing: 'on a white shelf against a plain sage wall, front view, the mug centered'},
        {key: 'lineart', label: 'Line art', style: 'a fine single-line drawing made with a porcelain paint pen', palette: 'black line on a matte cream mug', framing: 'on a linen napkin beside a wrapped gift box and ribbon, three-quarter view'},
    ],
    directions: ['coffee lover', 'Christmas', 'for dad'],
    directionHint: 'try: coffee lover',
    seo: {
        title: 'Mug painting ideas: face, floral, name mug',
        metaDescription: 'Nine mug painting ideas, a face mug, a floral mug and a mug that reads Mom, in pastel, bold and line-art styles.',
        h1: 'Mug painting ideas: face, floral, name mug',
        tagline: 'Three gift mugs, each in a pastel, a bold pop and a fine line-art style.',
        intro: 'A painted mug makes a personal gift. These nine show three designs: a mug with a cute face, a mug with daisies and a mug with the word Mom painted on it. Each design appears in three styles: soft pastel, bold pop with black outlines, and a fine single-line drawing. Each is shown on a table, on a shelf or beside a gift box. Type a message, such as coffee lover, to ask for it.',
        useCases: [
            "a birthday or Mother's Day gift",
            'a small gift for a Christmas stocking',
            'a desk mug for a family member',
            'a painted set for a friend',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'A face mug, a daisy mug and a mug reading Mom, each in a pastel, a bold or a line-art style.'},
            {q: 'Are these photos of real mugs?', a: 'No. They are generated pictures of each design, not photographs of a mug someone painted.'},
            {q: 'What is the line-art style?', a: 'A single black line drawn with a porcelain paint pen on a matte cream mug.'},
            {q: 'Can I ask for a different message?', a: 'Yes. Type a message such as coffee lover or for dad. A typed message is a request, not a promised result.'},
        ],
    },
}
